import React, { useState, useEffect, useRef } from 'react';
import { Tooltip } from 'syngenta-digital-cropwise-react-ui-kit';

interface CropSeasonDisplayProps {
  cropSeasonNames: string;
}

// Helper function to split and normalize season names
const splitSeasons = (cropSeasonNames: string): string[] => {
  return cropSeasonNames
    .split('|')
    .map(s => s.trim())
    .filter(s => s.length > 0)
    .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
};

// Helper function to create and configure measurement element
const createMeasurementElement = (): HTMLSpanElement => {
  const measureElement = document.createElement('span');
  measureElement.style.fontSize = '14px';
  measureElement.style.fontWeight = '400';
  measureElement.style.visibility = 'hidden';
  measureElement.style.position = 'absolute';
  measureElement.style.whiteSpace = 'nowrap';
  document.body.appendChild(measureElement);
  return measureElement;
};

// Helper function to calculate suffix width
const calculateSuffixWidth = (measureElement: HTMLSpanElement): number => {
  const suffixText = ' ... | +99 more'; // Placeholder for measurement (max digits)
  const element = measureElement;
  element.textContent = suffixText;
  return element.offsetWidth + 10; // Add some padding
};

// Helper function to check if full text fits
const checkFullTextFits = (
  measureElement: HTMLSpanElement,
  fullText: string,
  containerWidth: number
): boolean => {
  const element = measureElement;
  element.textContent = fullText;
  return element.offsetWidth <= containerWidth;
};

// Helper function to fit partial season
const fitPartialSeason = (
  measureElement: HTMLSpanElement,
  baseText: string,
  season: string,
  availableWidth: number
): string => {
  let partialSeason = '';
  const element = measureElement;

  for (let j = 1; j <= season.length; j += 1) {
    const testPartial = baseText + season.substring(0, j);
    element.textContent = testPartial;

    if (element.offsetWidth <= availableWidth) {
      partialSeason = season.substring(0, j);
    } else {
      break;
    }
  }
  return partialSeason;
};

// Helper function to create tooltip content with columns
const createTooltipContent = (seasons: string[]): React.ReactNode => {
  if (seasons.length === 0) return null;

  // Calculate number of columns needed (1 column per 10 seasons)
  const itemsPerColumn = 10;
  const numberOfColumns = Math.ceil(seasons.length / itemsPerColumn);

  // Split seasons into columns
  const columns: string[][] = [];
  for (let i = 0; i < numberOfColumns; i += 1) {
    const start = i * itemsPerColumn;
    const end = Math.min(start + itemsPerColumn, seasons.length);
    columns.push(seasons.slice(start, end));
  }

  // Calculate tooltip width based on number of columns
  const columnWidth = 200; // Base width per column
  const gap = 24; // Gap between columns
  const padding = 32; // Left and right padding (16px each)
  const tooltipWidth =
    columnWidth * numberOfColumns + gap * (numberOfColumns - 1) + padding;

  return (
    <div
      className="text-left p-2"
      style={{ minWidth: `${tooltipWidth}px`, width: `${tooltipWidth}px` }}
    >
      <div className="font-semibold mb-2">Crop Seasons</div>
      <div style={{ display: 'flex', gap: '24px', width: '100%' }}>
        {columns.map(column => (
          <ul
            key={`column-${column[0]}`}
            className="list-disc pl-4 space-y-1 ml-1"
            style={{ minWidth: '150px', flex: 1 }}
          >
            {column.map(season => (
              <li key={season} className="text-sm">
                <div
                  style={{
                    lineHeight: '1.4',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    wordBreak: 'break-word'
                  }}
                >
                  {season}
                </div>
              </li>
            ))}
          </ul>
        ))}
      </div>
    </div>
  );
};

// Helper function to truncate seasons to fit available width
const truncateSeasons = (
  measureElement: HTMLSpanElement,
  seasons: string[],
  availableWidth: number
): {
  displayText: string;
  visibleSeasonCount: number;
  hasPartialSeason: boolean;
} => {
  let displayText = '';
  let visibleSeasonCount = 0;
  let hasPartialSeason = false;
  const element = measureElement;

  for (let i = 0; i < seasons.length; i += 1) {
    const seasonText = i === 0 ? seasons[i] : ` | ${seasons[i]}`;
    const testText = displayText + seasonText;

    element.textContent = testText;
    if (element.offsetWidth <= availableWidth) {
      displayText = testText;
      visibleSeasonCount = i + 1;
    } else {
      const separator = i === 0 ? '' : ' | ';
      const baseText = displayText + separator;
      const partialSeason = fitPartialSeason(
        element,
        baseText,
        seasons[i],
        availableWidth
      );

      if (partialSeason.length > 0) {
        displayText = baseText + partialSeason;
        hasPartialSeason = true;
      }
      break;
    }
  }

  return { displayText, visibleSeasonCount, hasPartialSeason };
};

const CropSeasonDisplay: React.FC<CropSeasonDisplayProps> = ({
  cropSeasonNames
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [displaySeasons, setDisplaySeasons] = useState<string>('');
  const [hiddenCount, setHiddenCount] = useState<number>(0);
  const [hasPartialSeason, setHasPartialSeason] = useState<boolean>(false);

  useEffect(() => {
    const calculateTruncation = () => {
      if (!cropSeasonNames || !containerRef.current) {
        setDisplaySeasons('');
        setHiddenCount(0);
        setHasPartialSeason(false);
        return;
      }

      const seasons = splitSeasons(cropSeasonNames);

      if (seasons.length === 0) {
        setDisplaySeasons('');
        setHiddenCount(0);
        setHasPartialSeason(false);
        return;
      }

      const measureElement = createMeasurementElement();
      const containerWidth = containerRef.current.offsetWidth;
      const fullText = seasons.join(' | ');

      // Check if full text fits without truncation
      if (checkFullTextFits(measureElement, fullText, containerWidth)) {
        measureElement.remove();
        setDisplaySeasons(fullText);
        setHiddenCount(0);
        setHasPartialSeason(false);
        return;
      }

      // Need to truncate - find how much text fits with suffix
      const suffixWidth = calculateSuffixWidth(measureElement);
      const availableWidth = containerWidth - suffixWidth;

      const {
        displayText,
        visibleSeasonCount,
        hasPartialSeason: isPartial
      } = truncateSeasons(measureElement, seasons, availableWidth);

      measureElement.remove();

      setDisplaySeasons(displayText.trimEnd());
      setHasPartialSeason(isPartial);
      // A truncated season is visible on the first line (even if cut off), so
      // it does not count toward "+ N more". Only fully-hidden seasons do.
      // hasPartialSeason state is tracked separately so the badge still
      // renders when the partial is the only non-fully-visible season.
      const shownCount = visibleSeasonCount + (isPartial ? 1 : 0);
      setHiddenCount(Math.max(0, seasons.length - shownCount));
    };

    // Initial calculation
    calculateTruncation();

    // Set up ResizeObserver to recalculate on container resize
    const resizeObserver = new ResizeObserver(() => {
      calculateTruncation();
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    // Cleanup
    return () => {
      resizeObserver.disconnect();
    };
  }, [cropSeasonNames]);

  if (!cropSeasonNames) {
    return null;
  }

  const seasons = splitSeasons(cropSeasonNames);
  const tooltipContent = createTooltipContent(seasons);

  return (
    <div
      ref={containerRef}
      className="mb-3"
      style={{
        fontSize: '14px',
        fontStyle: 'normal',
        fontWeight: 400,
        color: '#14151C',
        overflow: 'hidden',
        whiteSpace: 'nowrap'
      }}
    >
      {displaySeasons}
      {hasPartialSeason && hiddenCount === 0 && <span>...</span>}
      {hiddenCount > 0 && (
        <span>
          {' '}
          {'... | '}
          <Tooltip
            title={tooltipContent}
            placement="topRight"
            color="#232630"
            overlayStyle={{ maxWidth: 'none' }}
            overlayInnerStyle={{ maxWidth: 'none' }}
          >
            <button
              type="button"
              className="crop-season-more-badge"
              aria-label={`Show ${hiddenCount} more crop season${
                hiddenCount > 1 ? 's' : ''
              }`}
              style={{
                display: 'inline-block',
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: 400,
                color: '#0071CD',
                cursor: 'pointer',
                padding: '4px',
                borderRadius: '2px',
                transition: 'background-color 0.2s',
                border: 'none',
                background: 'transparent'
              }}
              onMouseEnter={e => {
                e.currentTarget.style.backgroundColor = '#F3F4F6';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              +{hiddenCount} more
            </button>
          </Tooltip>
        </span>
      )}
    </div>
  );
};

export default CropSeasonDisplay;
